import React, { useContext, useState, useEffect } from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView } from 'react-native'
import { Text, Button, RadioGroup, Radio, Spinner } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import MoneyTextInput from '../../components/MoneyTextInput'
import { logout, saveCashboxTransaction } from '../../global/Firebase/firebase'
import { generateProductReportForDate } from '../../utils/validationFunctions'
import { countEmployeeBonuses } from '../../global/Firebase/workingHours'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom } from '../../global/AtomsProvider'
import { businessDayRangeForStore, getDailyStatID, getStoreBusinessDate } from '../../utils/timezoneFunctions'
import { parseDecimalAmount } from '../../utils/decimalInput'
// The WRITER's cash-wallet lookup. This screen kept a byte-identical private copy; it now shares
// the one definition, so the ATL-1393 fix to the writer cannot land on only half the close path.
import { getCashWallet } from './systemEndAmounts'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

export const TRANSACTION_TYPE = [
  { id: 'openShift' },
  { id: 'withdrawal' },
  { id: 'deposit' },
  { id: 'closeShift' },
]

export function CashboxTransactionScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const { close, systemEndAmounts, store } = route.params
  const constants = useAtomValue(constantsAtom)
  const postHog = usePostHog()
  const [endAmounts, setEndAmounts] = useState({})
  const [amount, setAmount] = useState('')
  const [type, setType] = useState(null)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // ATL-2001: the report is of the shift being closed — the store's BUSINESS day. The device's
    // calendar day is the next business day between midnight and the cutoff.
    generateProductReportForDate(getStoreBusinessDate(store), store)
  }, [])

  const save = async () => {
    // ATL-1371 — cash in/out of the drawer. `parseFloat` read '36,5' from a ru/kk keypad as 36.
    const numberAmount = parseDecimalAmount(amount)
    if (numberAmount === null) {
      setSaveError(t('shift.notANumberError'))
      setIsLoading(false)
      return
    }
    if (type != null) {
      setIsLoading(true)
      setSaveError('')
      try {
        const wallet = getCashWallet(constants?.paymentMethods?.data, store)
        await saveCashboxTransaction(db, {
          amount: numberAmount,
          date: new Date(),
          dailyStatID: getDailyStatID(store, new Date()),
          cashierUID: context.user.uid,
          type: TRANSACTION_TYPE[type],
          store: store,
          wallet: wallet,
          organizationId: context.user.organizationId
        })
        setIsLoading(false)
        setSuccessMessageVisible(true)
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'cashbox-transaction-create' } })
        console.log(error)
        setSaveError(saveFailureText(error, t, t('error')))
      }
    } else {
      setSaveError(t('notAllFieldsFilled'))
    }
  }

  const putEndAmounts = (textValue, id) => {
    var newValues = {...endAmounts}
    newValues[id] = textValue
    setEndAmounts(newValues)
  }

  const closeShift = async () => {
    setIsLoading(true)
    setSaveError('')
    if (Object.keys(endAmounts).length < constants?.paymentMethods.data.length) {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
      return
    }
    var numberEndAmounts = {}
    const cashWallet = getCashWallet(constants?.paymentMethods?.data, store)
    for (var key in endAmounts) {
      let amount = endAmounts[key]
      // ATL-1371 — the counted cash at close of shift, per payment method. Same strict parse.
      let numberAmount = parseDecimalAmount(amount)
      if (numberAmount === null) {
        setSaveError(t('shift.notANumberError'))
        setIsLoading(false)
        return
      }
      // For cash payment method, use wallet ID instead of payment method ID
      if (key === 'cash') {
        numberEndAmounts[cashWallet] = numberAmount
      } else {
        numberEndAmounts[key] = numberAmount
      }
    }
    // Not awaited, as before. A W refusal (staging cut-over preview) was already announced by the guard;
    // anything else rethrows as before (P9b review 1).
    // ATL-2001: count the bonuses of the business day being closed. A calendar-day range here was
    // the NEXT business day between midnight and the cutoff, so a 00:04 close read none of the
    // day's working hours and counted no bonuses for it.
    countEmployeeBonuses(null, businessDayRangeForStore(store), store, null, null).catch(rethrowUnlessWRefusal)
    try {
      await saveCashboxTransaction(db, {
        endAmounts: numberEndAmounts,
        systemEndAmounts,
        date: new Date(),
        dailyStatID: getDailyStatID(store, new Date()),
        cashierUID: context.user.uid,
        type: TRANSACTION_TYPE[3],
        employeesOnShift: context.employeesOnShift,
        store: store,
        organizationId: context.user.organizationId
      })
      sendFBAnalyticsEvent('close_shift')
      postHog.capture('shift_closed')
      setIsLoading(false)
      setSuccessMessageVisible(true)
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'shift-close' } })
      setIsLoading(false)
      console.log(error)
      setSaveError(saveFailureText(error, t, t('error')))
    }
  }
  
  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      { close ? (
        <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
          <Text category='h2' style={styles.label}>{t('shift.closeShift')}</Text>
          { constants?.paymentMethods.data.map((item, index) => (
            <View key={item+index}>
              <Text category='label' style={styles.label}>{item.name} - {t('shift.endDayAmount')}</Text>
              <MoneyTextInput
                value={endAmounts[item.id]}
                onChangeText={text => putEndAmounts(text, item.id)}
                style={styles.priceField}
                returnKeyType='done'
              />
            </View>
          ))}
          {saveError != '' && (
            <Text category='p1' status='danger' style={styles.label}>
              {saveError}
            </Text>
          )}
          { successMessageVisible && (
            <>
              <Text status='success'>{t('success')}</Text>
              <Button appearance='outline' onPress={() => logout()} style={styles.submitButton}>
                {t('shift.logout')}
              </Button>
              <Button appearance='outline' onPress={() => navigation.goBack()} style={styles.submitButton}>
                {t('back')}
              </Button>
            </>
          )}
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            !successMessageVisible && <View testID="cashbox-close"><Button style={styles.submitButton} onPress={closeShift}>{t('shift.close')}</Button></View>
          )}
          <View style={{height: 100}}></View>
        </ScrollView>
      ) : (
        <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
          <RadioGroup
            selectedIndex={type}
            onChange={(index) => {
              setType(index)
            }}
          >
            {TRANSACTION_TYPE.map((item, index) => (
              <Radio key={item.id} style={styles.radio} disabled={index === 0 || index === 3}>
                {t(`shift.${item.id}`)}
              </Radio>
            ))}
          </RadioGroup>
          <Text category='label' style={styles.label}>{t('shift.amount')}</Text>
          <View testID="cashbox-amount">
            <MoneyTextInput
              value={amount}
              onChangeText={text => setAmount(text)}
              style={styles.priceField}
              returnKeyType='done'
            />
          </View>
          {saveError != '' && (
            <Text category='p1' status='danger' style={styles.label}>
              {saveError}
            </Text>
          )}
          { successMessageVisible && (
            <>
              <Text status='success'>{t('success')}</Text>
              <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
                {t('back')}
              </Button>
            </>
          )}
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            !successMessageVisible && <View testID="cashbox-save"><Button style={styles.submitButton} onPress={save}>{t('save')}</Button></View>
          )}
          <View style={{height: 100}}></View>
        </ScrollView>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  }, 
  label: {
    marginTop: 15,
    marginBottom: 5,
  },
  submitButton: {
    marginTop: 15,
  },
  priceField: {
    marginBottom: 10,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  }
});




